import * as React from 'react';
import StyledPdpContainer from './StyledPdpContainer';
import {
  ExperimentProvider,
  FeatureOverrideProvider,
  StringsProvider,
  ThemeProvider,
  usePostMessageContext,
  useContractContext,
  useAnalytics,
  usePostMessageHandler,
  useFeatures,
  useConfig,
} from 'src/providers';

import { useConfigOption } from 'src/hooks/useConfigOption';
import { useCustomText } from 'src/hooks/useCustomText';
import { useInitializedEvent } from 'src/hooks/useInitializedEvent';
import { useOuterContentHeightChanged } from 'src/hooks/useOuterContentHeightChanged';
import { useResizeObserver } from 'src/hooks/useResizeObserver';
import { useTrackOnce } from 'src/hooks/useTrackOnce';
import { useVisible } from 'src/hooks/useVisible';
import { useCustomStyleSheet } from 'src/hooks/useCustomStyleSheet';

import { VerticalPrompt } from 'src/components/Prompts/Vertical';
import { HorizontalPrompt } from 'src/components/Prompts/Horizontal';
import { Simple } from 'src/components/Prompts/Simple';
import { ValueProp } from 'src/components/Prompts/ValueProp';
import { ContractDetailsRequestedPromptOptions } from 'src/types/IModal';

const PDP_VERSION = '1.0.0';

export function PDP() {
  const { track } = useAnalytics();
  const { postMessage } = usePostMessageContext();
  const { contracts, protectionAdded } = useContractContext();
  const [impressionEventFired, setImpressionEventFired] = React.useState(false);
  const trackScrolledToOnce = useTrackOnce();
  const promptType = useConfigOption('type', 'simple');
  const { showValuePropCta, reducedDecisionModal } = useFeatures(); 
  const { phrases } = useConfig();

  useCustomStyleSheet();

  const contractDetailsRequested = React.useCallback(
    (prompt: ContractDetailsRequestedPromptOptions) => {
      postMessage('openModal', {
        cta: 'PDP',
        prompt,
        modalDropdown: reducedDecisionModal,
      });

      if (prompt === 'simple') {
        postMessage('simpleAddProtection', {
          ctaType: 'productPage',
          productPageType: prompt,
        });
      }
    },
    [postMessage]
  );

  // Send initialized event on first render
  useInitializedEvent({
    frame: 'productPage',
    version: PDP_VERSION,
  });

  usePostMessageHandler('contractAddedToCart', (e) => {
    const { source, selectedContractSku, termSelected } = e.detail;
    if (source === 'productPage') {
      track('contract_added_to_cart', {
        ctaVersion: '1.0.0',
        type: 'productPage',
        productPageType: promptType,
        selectedContractSku,
        termSelected,
      });
    }
  });

  // This is really important for the impression event to fire.
  // It must fire once, and it must fire after we get contracts
  React.useEffect(() => {
    if (contracts && contracts.length && !impressionEventFired) {
      postMessage('contractsBuilt', {
        frame: 'productPage',
        productPageType: promptType,
        version: PDP_VERSION,
      });
      track('impression', {
        ctaVersion: PDP_VERSION,
        type: 'productPage', // TODO: should this be product_page?!
        productPageType: promptType,
        // NOTE: in the parent scripts, we check(ed) whether the iframe was visible using display/visibility.
        // But, we've already gated this Effect to only fire if there are contracts, so it should effectively
        // always be = TRUE
        visible: true, 
      });
      setImpressionEventFired(true);
    }
  }, [contracts, impressionEventFired, setImpressionEventFired]);

  // Watch for PDP to become visible
  const observeVisibility = useVisible(({ visible }) => {
    if (!visible) {
      return true;
    }

    // NOTE: this is very defensive. Only track the event once,
    // but also tell the observer to disconnect
    trackScrolledToOnce('scrolled_to', {
      ctaVersion: '1.0.0',
      type: 'productPage',
      productPageType: promptType,
    });
    return false;
  });

  // Observe content height changes and inform the parent frame
  const postOuterContentHeightChanged = useOuterContentHeightChanged();
  const observeSize = useResizeObserver(React.useCallback(({ height }) => {
    postOuterContentHeightChanged(height);
  }, [postOuterContentHeightChanged]));

  const pdpRef = (el: HTMLDivElement | null) => {
    observeVisibility(el);
    observeSize(el);
  };

  const invertCtaColors = React.useMemo(() => {
    // Invert SIMPLE PROMPT when protection is added for the "will be added to cart" effect
    // TODO: this logic is confusing
    return promptType !== 'simple' || protectionAdded !== true;
  }, [promptType, protectionAdded]);

  // If only one contract we render single radio prompt below (styled as checkbox)
  const hasSingleContract = React.useMemo(
    () => contracts && contracts.length === 1,
    [contracts, promptType]
  );

  // NOTE: this could be cleaned up at some point. maybe a hook or a wrapper around StringsProvider?
  const simplePromptLabel = useCustomText(
    'pagePromptHeadline',
    hasSingleContract ? phrases['pdp.simpleAddSingle'] : phrases['pdp.simpleAddMultiple'],
  );
  const simplePromptAdded = useCustomText('simplePromptAdded', phrases['pdp.simpleAdded']);
  const simpleButtonText = React.useMemo(() => {
    return protectionAdded ? simplePromptAdded : simplePromptLabel;
  }, [protectionAdded, simplePromptAdded, simplePromptLabel]);
  const declineOption = useCustomText('pdpDeclineOption', phrases['pdp.declineOption']);
  const headline = useCustomText('pagePromptHeadline', phrases['pdp.headline']);

  const strings = React.useMemo(() => ({
    simpleButtonText,
    declineOption,
    headline,
    learnMoreLink: phrases['pdp.learnMoreButton'],
    termLabel: promptType === 'radio' || showValuePropCta
      ? phrases['shared.termProtectionPlan']
      : phrases['shared.termSingular'],
  }), [phrases, showValuePropCta, promptType, simpleButtonText]);

  const renderPrompt = React.useCallback(() => {
    if (!contracts || contracts.length === 0) {
      return null;
    }
    const maybeOverridePromptType = showValuePropCta
      ? 'value-prop'
      : promptType;

    switch (maybeOverridePromptType) {
      case 'list':
        return (
          <VerticalPrompt
            contractDetailsRequested={contractDetailsRequested}
            isList={!hasSingleContract}
          />
        );
      case 'radio':
        return <VerticalPrompt contractDetailsRequested={contractDetailsRequested} />;
      case 'select':
        return hasSingleContract ? (
          <VerticalPrompt contractDetailsRequested={contractDetailsRequested} />
        ) : (
          <HorizontalPrompt contractDetailsRequested={contractDetailsRequested} />
        );
      case 'value-prop':
        return <ValueProp contractDetailsRequested={contractDetailsRequested} />;
      case 'simple':
      default: {
        return <Simple onClick={() => contractDetailsRequested(ContractDetailsRequestedPromptOptions.SIMPLE)} />;
      }
    }
  }, [contracts, promptType, showValuePropCta]);

  // IMPORTANT: pdpRef element needs to stay mounted, even if contracts change,
  // so make sure NOT to shortcircuit this top-level render function
  return (
    <FeatureOverrideProvider invertColors={invertCtaColors}>
      <ThemeProvider fullWidth>
        <StringsProvider {...strings}>
          <ExperimentProvider>
            <StyledPdpContainer className="clyde-pdp" ref={pdpRef}>
              {renderPrompt()}
            </StyledPdpContainer>
          </ExperimentProvider>
        </StringsProvider>
      </ThemeProvider>
    </FeatureOverrideProvider>
  );
}
